// @ts-strict-ignore
import React, { useContext } from 'react';
import {
  ErrorBoundary,
  Icon, IconName, Tooltip,
} from '@usealloy/component-library';
import type { Tag as TagType } from '~/application-review/ReviewAlertModal/ReviewAlertModal';
import BaseNode from '~/dashboard/views/workflows/editor/components/Graph/nodes/BaseNode';
import { StyledIconWrapper } from '~/dashboard/views/workflows/editor/components/Graph/nodes/TagNode/styles';
import { getIconColorForBackgroundColor } from '~/dashboard/views/workflows/editor/components/helpers';
import { useModal } from '~/journey/ModalContext';
import TagRuleModal from '../TagRuleModal';
import type { Dimension } from '~/application-review/redux/types';
import { useInitializeWorkflowRedux } from 'flow/editor/hooks/useInitializeWorkflowRedux';
import { UserContext } from '~/dashboard/context';
import { useAppSelector } from '~/store/reduxHooks';
import { selectWorkflowData } from 'flow/store/redux/selectors/general';
import log from '~/helpers/logging';

type TagNodeInReviewProps = {
  tag: TagType;
  outcome: string;
  entityToken: string;
  evaluationTags: Array<{ tag_name: string }>;
  evaluationToken: string;
  isPortfolioEvaluation: boolean;
  dimensions: Dimension[];
  workflowToken: string;
  workflowVersion: number;
};

const TagNodeInReview = ({
  tag,
  outcome,
  entityToken,
  evaluationTags,
  evaluationToken,
  isPortfolioEvaluation,
  dimensions,
  workflowToken,
  workflowVersion,
}: TagNodeInReviewProps) => {
  const { open, close } = useModal();
  const { featureFlags } = useContext(UserContext);
  const workflowData = useAppSelector(selectWorkflowData);

  let iconName: IconName = null;
  if (tag.is_outcome_reason) {
    iconName = IconName.Stamp;
  } else if (tag.is_case_creating) {
    iconName = IconName.Opened;
  }

  const iconColor = getIconColorForBackgroundColor(tag.tag_color);

  useInitializeWorkflowRedux({
    workflowToken,
    versionId: workflowVersion,
    featureFlags,
  });

  const workflowLoading = !workflowData?.actions;

  return (
    <Tooltip
      content={tag.tag_name}
      side="top"
    >
      <ErrorBoundary
        bannerErrorText="Error showing tag rule"
        withResetChildren
        loggingCallback={error => {
          log('error', 'Error showing tag rule', { url: window.location.pathname }, error);
        }}
      >
        <div
          id={`alloydd-explainability-tag_${tag.tag_name}`}
          data-dd-action-name="alloydd-explainability-tag"
          style={{
            opacity: workflowLoading ? 0.5 : 1,
            pointerEvents: workflowLoading ? 'none' : 'auto',
            cursor: workflowLoading ? 'not-allowed' : 'pointer',
          }}
          onClick={() => {
          // Prevent opening modal when workflow is loading
            if (workflowLoading) return;

            open(
              <TagRuleModal
                actionName={tag.tag_name}
                outcome={outcome}
                tagDescription={tag.description}
                closeModal={close}
                entityToken={entityToken}
                evaluationToken={evaluationToken}
                isPortfolioEvaluation={isPortfolioEvaluation}
                dimensions={dimensions}
                evaluationTags={evaluationTags}
                workflowToken={workflowToken}
                workflowVersion={workflowVersion}
              />,
              {
                isComponentLibraryModal: true,
              },
            );
          }}
        >
          <BaseNode
            id={`${tag.customer_tag_id || tag.tag_name}`}
            label={tag.tag_name}
            type={null}
            icon={(
              <StyledIconWrapper backgroundColor={tag.tag_color}>
                {iconName === IconName.Stamp && (
                <Icon color={iconColor} icon={iconName} size="small" />
                )}
                {iconName === IconName.Opened && (
                <Icon color={iconColor} icon={iconName} size="large" />
                )}
              </StyledIconWrapper>
          )}
            stateStyle={{
              baseBackgroundColor: 'var(--cl-gray-300)',
              hovered: {
                backgroundColor: 'var(--cl-gray-400)',
              },
              highlighted: {
                backgroundColor: 'var(--cl-gray-400)',
              },
            }}
            isOutsideCanvas
          />
        </div>
      </ErrorBoundary>
    </Tooltip>
  );
};

export default TagNodeInReview;
